// Minimal export helpers: CSV and Excel (xlsx) fallback to CSV if SheetJS not available

export function exportTableToCSV(rowsArray, columns, filename = 'export.csv', firstHeaderLabel = '') {
  // rowsArray: array of rows where each row is [label, ...cells, total]
  // columns: array of column objects with {id,label} (labels already localized)
  const rows = [];
  const header = [firstHeaderLabel || 'Label', ...columns.map(c => c.label ?? c.id)];
  rows.push(header);

  // rowsArray already contains label + values
  rowsArray.forEach(r => rows.push(r.map(cell => cell == null ? '' : String(cell))));

  const csvContent = rows.map(r => r.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function exportTableToExcel(rowsArray, columns, filename = 'export.xlsx', firstHeaderLabel = '') {
  // rowsArray: array of rows [label, ...cells, total]
  try {
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sheet1');

    // header
    const header = [firstHeaderLabel || 'Label', ...columns.map(c => c.label ?? c.id)];
    sheet.addRow(header);
    // style header row
    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } }; // blue

    // body rows with alternating fill
    let rowIndex = 2;
    rowsArray.forEach(r => {
      const row = sheet.addRow(r);
      if (rowIndex % 2 === 0) {
        row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
      }
      rowIndex++;
    });

    // autosize columns a bit
    sheet.columns.forEach(col => {
      col.width = Math.max(10, (col.header ? String(col.header).length : 10) + 2);
    });

    // write file (browser)
    const buf = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  } catch (err) {
    console.warn('exceljs not available or failed, falling back to xlsx', err);
  }

  // fallback to SheetJS (xlsx)
  try {
    const XLSX = await import('xlsx');
    const ws_data = [];
    const header = [firstHeaderLabel || 'Label', ...columns.map(c => c.label ?? c.id)];
    ws_data.push(header);
    rowsArray.forEach(r => {
      ws_data.push(r.map(cell => cell == null ? '' : cell));
    });
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename);
  } catch (err) {
    console.warn('xlsx not available, falling back to CSV', err);
    exportTableToCSV(rowsArray, columns, filename.replace(/\.xlsx?$/, '.csv'), firstHeaderLabel);
  }
}
